package com.downdogapp.client.singleton

import kotlin.jvm.JvmName

object UserPrefs : SharedUserPrefsInterface by UserPrefsHelper {

  operator fun get(keyString: Key<String>) = UserPrefsHelper.getString(
    keyString.name
  )

  operator fun get(keyDouble: Key<Double>) = UserPrefsHelper.getDouble(
    keyDouble.name
  )

  operator fun get(keyInt: Key<Int>) = UserPrefsHelper.getInt(
    keyInt.name
  )

  operator fun get(keyLong: Key<Long>) = UserPrefsHelper.getLong(
    keyLong.name
  )

  operator fun get(keyBoolean: Key<Boolean>) = UserPrefsHelper.getBoolean(
    keyBoolean.name
  )

  private fun <T> get(keyList: Key<List<T>>, transform: (String) -> T?) =
    UserPrefsHelper.getString(keyList.name).let {
      if (it.isNullOrEmpty())
        listOf()
      else
        it.split(listSeparator).mapNotNull { transform(it) }
    }

  @JvmName("getStringList")
  operator fun get(keyStringList: Key<List<String>>) =
    get(keyStringList) { it }

  @JvmName("getIntList")
  operator fun get(keyIntList: Key<List<Int>>) =
    get(keyIntList) { it.toInt() }

  operator fun set(key: Key<String>, string: String?) = UserPrefsHelper.set(
    key.name,
    string
  )

  operator fun set(key: Key<Double>, double: Double) = UserPrefsHelper.set(
    key.name,
    double
  )

  operator fun set(key: Key<Int>, int: Int) = UserPrefsHelper.set(
    key.name,
    int
  )

  operator fun set(key: Key<Long>, long: Long) = UserPrefsHelper.set(
    key.name,
    long
  )

  operator fun set(key: Key<Boolean>, boolean: Boolean) = UserPrefsHelper.set(
    key.name,
    boolean
  )

  operator fun <T> set(key: Key<List<T>>, list: List<T>) =
    UserPrefsHelper.set(
      key.name,
      list.joinToString(listSeparator)
    )

  fun remove(key: Key<*>) = UserPrefsHelper.remove(
    key.name
  )

  private val listSeparator = "#^$"
}

interface SharedUserPrefsInterface {

  fun clear()
}

sealed class Key<T>(val name: String) {

  object AcceptedHealthWaiver : Key<Boolean>("acceptedHealthWaiver")
  object DisplayPoseNames : Key<Boolean>("displayPoseNames")
  object showLikeAndExcludeButtons : Key<Boolean>("showLikeAndExcludeButtons")
  object ShowSubtitles : Key<Boolean>("showSubtitles")
  object ShowOverlay : Key<Boolean>("showOverlay")
  object ShowCountdown : Key<Boolean>("showCountdown")
  object KeepTimelineVisible : Key<Boolean>("keepTimelineVisible")
  object ShowPosesOnTimeline : Key<Boolean>("showPosesOnTimeline")
  object MirrorVideo : Key<Boolean>("mirrorVideo")
  object VideoQualityId : Key<Int>("videoQualityId")

  object AudioBalance : Key<Double>("audioBalance")

  object StartNumViews : Key<Int>("startNumViews")
  object LoadingNumViews : Key<Int>("loadingNumViews")
  object FirstDayOfWeekId : Key<Int>("firstDayOfWeekId")
  object WeeklyGoal : Key<Int>("weeklyGoal")
  object StatTypeId : Key<Int>("statTypeId")
  object MixOnlyGroup : Key<Int>("mixOnlyGroup")
  object MixPreset : Key<Int>("mixSelectedPreset")
  object CustomMix : Key<Boolean>("customMix")

  object Cred : Key<String>("cred")
  object Manifest : Key<String>("manifestJson6.8")
  object Language : Key<String>("language")
  object LinkedSequenceId : Key<String>("linkedSequenceId")
  object LinkedPracticeId : Key<String>("linkedPracticeId")

  object ReferredByUserId : Key<String>("referredByUserId")
  object ReferredByPromoLinkId : Key<String>("referredByPromoLinkId")
  object ReferredByAffiliateId : Key<String>("referredByAffiliateId")
  object CorporatePlanId : Key<String>("corporatePlanId")

  // Types for these two arrays are defined in OfflinePractices.kt
  // OfflineDownloadsInProgress is a JSON array of DownloadInProgress
  object OfflineDownloadsInProgress : Key<String>("offlineDownloadsInProgress")

  // OfflinePractices is a JSON array of OfflinePractice
  object OfflinePractices : Key<String>("offlinePractices")

  object PendingRequestIds : Key<List<String>>("pendingRequestIds6.8")
  object RecentSettingSelectorTypeIds : Key<List<Int>>("recentSettingSelectorTypeIds")

  object LikedPoseCount : Key<Int>("likedPoseCount")
  object DislikedPoseCount : Key<Int>("dislikedPoseCount")

  class PendingRequest(id: String) : Key<String>("pendingRequest$id")
  class SequenceSetting(id: Int) : Key<Int>("sequenceSetting$id")
  class MixGroupAmount(groupId: Int) : Key<Int>("mixGroupAmount$groupId")
  class MixSubgroupEnabled(subgroupId: Int) : Key<Boolean>("mixSubgroupEnabled$subgroupId")
}

expect object UserPrefsHelper : SharedUserPrefsInterface {

  fun getString(key: String): String?

  fun getDouble(key: String): Double?

  fun getInt(key: String): Int?

  fun getLong(key: String): Long?

  fun getBoolean(key: String): Boolean?

  operator fun set(key: String, string: String?)

  operator fun set(key: String, double: Double)

  operator fun set(key: String, int: Int)

  operator fun set(key: String, long: Long)

  operator fun set(key: String, boolean: Boolean)

  fun remove(key: String)
}
